import { useState, useEffect, type FormEvent } from 'react';
import { Card, CardContent } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import {
  UserPlus,
  Search,
  X,
  Copy,
  Check,
  RefreshCw,
  Trash2,
  Shield,
  AlertTriangle,
  UserX,
  UserCheck,
  Clock,
  Activity,
} from 'lucide-react';
import { api } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { SortButton } from '@/components/ui/SortButton.js';
import { Pagination } from '@/components/ui/Pagination.js';
import { DeleteConfirmModal } from '@/components/ui/DeleteConfirmModal.js';
import { SkeletonCard } from '@/components/ui/skeleton.js';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar_url?: string | null;
  timezone: string;
  is_active?: boolean;
  projects_count: number;
  active_tasks_count: number;
  created_at?: string;
  last_login_at?: string | null;
  last_login_ip?: string | null;
  last_login_user_agent?: string | null;
}

export interface CapacityRecord {
  id: string;
  name: string;
  email: string;
  role: string;
  active_tasks: number;
  weekly_hours: number;
  baseline_hours: number;
  capacity_status: 'Optimal' | 'Heavy' | 'Overloaded';
  utilization_percent: number;
}

export interface CapacitySummary {
  total_members: number;
  optimal_count: number;
  heavy_count: number;
  overloaded_count: number;
  total_active_tasks: number;
  total_weekly_hours: number;
}

export function formatRelativeLastLogin(isoString?: string | null): string {
  if (!isoString) return 'Never logged in';
  const diffMs = Date.now() - new Date(isoString).getTime();
  if (diffMs < 0 || isNaN(diffMs)) return 'Recently';
  const min = Math.floor(diffMs / (60 * 1000));
  if (min < 1) return 'Active just now';
  if (min < 60) return `Active ${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `Active ${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `Active ${days}d ago`;
  return `Active ${new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

function generatePassword(length = 12): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*';
  let pass = '';
  for (let i = 0; i < length; i++) {
    pass += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return pass;
}

export function TeamPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [capacityData, setCapacityData] = useState<CapacityRecord[]>([]);
  const [capacitySummary, setCapacitySummary] = useState<CapacitySummary | null>(null);
  const [capacityFilter, setCapacityFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [sortField, setSortField] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Invite Modal State
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    role: 'team_member',
    password: generatePassword(),
  });
  const [copied, setCopied] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);

  // Member Detail Drawer State
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [drawerRole, setDrawerRole] = useState<string>('team_member');
  const [actionError, setActionError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Delete Confirmation State
  const [deleteMemberTarget, setDeleteMemberTarget] = useState<TeamMember | null>(null);

  // Close modals on Escape key
  useEffect(() => {
    if (!showInviteModal && !selectedMember) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowInviteModal(false);
        setSelectedMember(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showInviteModal, selectedMember]);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const loadTeam = async () => {
    try {
      const [teamRes, capRes] = await Promise.allSettled([
        api.get<{ data: TeamMember[] }>('/api/v1/team'),
        api.get<{ data: CapacityRecord[]; summary: CapacitySummary }>('/api/v1/team/capacity'),
      ]);

      if (teamRes.status === 'fulfilled') {
        const res = teamRes.value as any;
        const list = res?.data || (Array.isArray(res) ? res : []);
        setMembers(list);
      }
      if (capRes.status === 'fulfilled') {
        const res = capRes.value as any;
        const capList = res?.data || (Array.isArray(res) ? res : []);
        setCapacityData(capList);
        if (res?.summary) {
          setCapacitySummary(res.summary);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeam();
  }, []);

  const handleOpenInvite = () => {
    setInviteForm({
      name: '',
      email: '',
      role: 'team_member',
      password: generatePassword(),
    });
    setCopied(false);
    setInviteError(null);
    setShowInviteModal(true);
  };

  const handleRegeneratePassword = () => {
    setInviteForm((prev) => ({ ...prev, password: generatePassword() }));
    setCopied(false);
  };

  const handleCopyPassword = () => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(inviteForm.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleInviteSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!inviteForm.name.trim() || !inviteForm.email.trim() || !inviteForm.password.trim()) {
      setInviteError('Please fill in all required fields.');
      return;
    }
    setInviteSubmitting(true);
    setInviteError(null);

    try {
      try {
        await api.post('/api/v1/team', {
          name: inviteForm.name.trim(),
          email: inviteForm.email.trim(),
          role: inviteForm.role,
          password: inviteForm.password,
        });
      } catch {
        await api.post('/api/v1/users', {
          name: inviteForm.name.trim(),
          email: inviteForm.email.trim(),
          role: inviteForm.role,
          password: inviteForm.password,
        });
      }

      setShowInviteModal(false);
      showToast(`Invited ${inviteForm.name} successfully!`);
      await loadTeam();
    } catch (err: any) {
      setInviteError(err.message || 'Failed to invite team member');
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleOpenDetail = (member: TeamMember) => {
    setSelectedMember(member);
    setDrawerRole(member.role);
    setActionError(null);
  };

  const handleCloseDetail = () => {
    setSelectedMember(null);
    setActionError(null);
  };

  const handleUpdateRole = async () => {
    if (!selectedMember) return;
    setActionError(null);
    try {
      try {
        await api.patch(`/api/v1/team/${selectedMember.id}`, { role: drawerRole });
      } catch {
        await api.patch(`/api/v1/users/${selectedMember.id}/role`, { role: drawerRole });
      }
      showToast(`Role updated to ${drawerRole}`);
      setSelectedMember((prev) => (prev ? { ...prev, role: drawerRole } : null));
      await loadTeam();
    } catch (err: any) {
      setActionError(err.message || 'Failed to update member role');
    }
  };

  const handleToggleDeactivate = async () => {
    if (!selectedMember) return;
    const currentActive = selectedMember.is_active !== false;
    const willDeactivate = currentActive;

    const confirmed = window.confirm(
      willDeactivate
        ? `Are you sure you want to deactivate ${selectedMember.name}? They will not be able to log in.`
        : `Activate ${selectedMember.name}?`
    );
    if (!confirmed) return;

    setActionError(null);
    try {
      try {
        await api.patch(`/api/v1/team/${selectedMember.id}`, { is_active: !willDeactivate });
      } catch {
        await api.patch(`/api/v1/users/${selectedMember.id}`, { is_active: !willDeactivate });
      }
      showToast(willDeactivate ? 'Member deactivated.' : 'Member activated.');
      setSelectedMember((prev) => (prev ? { ...prev, is_active: !willDeactivate } : null));
      await loadTeam();
    } catch (err: any) {
      setActionError(err.message || 'Failed to toggle member active status');
    }
  };

  const handleRemoveMember = () => {
    if (!selectedMember) return;
    setDeleteMemberTarget(selectedMember);
  };

  const confirmRemoveMember = async () => {
    if (!deleteMemberTarget) return;
    setActionError(null);
    try {
      try {
        await api.delete(`/api/v1/team/${deleteMemberTarget.id}`);
      } catch {
        await api.delete(`/api/v1/users/${deleteMemberTarget.id}`);
      }
      showToast(`${deleteMemberTarget.name} removed successfully.`);
      setDeleteMemberTarget(null);
      handleCloseDetail();
      await loadTeam();
    } catch (err: any) {
      alert(err.message || 'Failed to remove team member');
    }
  };

  // Filter & Sorting
  const filtered = members.filter((m) => {
    const q = search.toLowerCase();
    const matchesSearch = q ? m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q) || m.role.toLowerCase().includes(q) : true;
    const matchesRole = roleFilter ? m.role === roleFilter : true;
    const cap = capacityData.find((c) => c.id === m.id);
    const matchesCapacity = capacityFilter !== 'all' ? cap?.capacity_status === capacityFilter : true;
    return matchesSearch && matchesRole && matchesCapacity;
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'last_login_at' ? 'desc' : 'asc');
    }
  };

  const sortedMembers = [...filtered].sort((a: any, b: any) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    if (sortField === 'last_login_at') {
      if (!aVal && !bVal) return 0;
      if (!aVal) return 1;
      if (!bVal) return -1;
      const aTime = new Date(aVal).getTime();
      const bTime = new Date(bVal).getTime();
      return sortDir === 'asc' ? aTime - bTime : bTime - aTime;
    }

    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';
    if (typeof aVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  const pageSize = 25;
  const totalPages = Math.max(1, Math.ceil(sortedMembers.length / pageSize));
  const validCurrentPage = Math.min(currentPage, totalPages);
  const paginatedMembers = sortedMembers.slice((validCurrentPage - 1) * pageSize, validCurrentPage * pageSize);

  return (
    <div className="space-y-6">
      {/* Toast Feedback */}
      {toastMessage && (
        <div className="fixed top-16 right-6 z-50 bg-brand-navy text-white px-4 py-2.5 rounded-lg shadow-xl text-xs flex items-center space-x-2 animate-in fade-in slide-in-from-top-2">
          <Check className="h-4 w-4 text-brand-teal" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Team Directory & Workload</h1>
          <p className="helper-text mt-1">Employee roles, assigned project capacity, and active task allocations.</p>
        </div>
        {isOwner && (
          <Button
            onClick={handleOpenInvite}
            className="flex items-center space-x-1.5 font-semibold bg-brand-teal text-white hover:bg-brand-teal-action"
          >
            <UserPlus className="h-4 w-4" />
            <span>Invite Team Member</span>
          </Button>
        )}
      </div>

      {/* Developer Workload & Capacity Heatmap Panel */}
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-teal-50/30 p-5 shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center space-x-2.5">
            <div className="p-2 rounded-xl bg-teal-100 text-teal-700">
              <Activity className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h2 className="text-sm sm:text-base font-bold text-brand-navy">
                  Developer Workload & Capacity Heatmap
                </h2>
                <Badge variant="outline" className="font-mono text-[10px] text-teal-700 border-teal-300">
                  40h Agency Baseline
                </Badge>
              </div>
              <p className="text-xs text-slate-500">
                Live capacity balancing across assigned tasks and tracked hours to preempt delivery bottlenecks.
              </p>
            </div>
          </div>

          {/* Quick Filter Buttons */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => { setCapacityFilter('all'); setCurrentPage(1); }}
              className={`px-2.5 py-1 text-xs rounded-lg font-medium transition-colors ${
                capacityFilter === 'all'
                  ? 'bg-slate-800 text-white shadow-xs'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              All ({members.length})
            </button>
            <button
              onClick={() => { setCapacityFilter('Optimal'); setCurrentPage(1); }}
              className={`px-2.5 py-1 text-xs rounded-lg font-medium transition-colors ${
                capacityFilter === 'Optimal'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100'
              }`}
            >
              🟢 Optimal ({capacitySummary?.optimal_count || 0})
            </button>
            <button
              onClick={() => { setCapacityFilter('Heavy'); setCurrentPage(1); }}
              className={`px-2.5 py-1 text-xs rounded-lg font-medium transition-colors ${
                capacityFilter === 'Heavy'
                  ? 'bg-amber-600 text-white shadow-xs'
                  : 'bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100'
              }`}
            >
              🟡 Heavy ({capacitySummary?.heavy_count || 0})
            </button>
            <button
              onClick={() => { setCapacityFilter('Overloaded'); setCurrentPage(1); }}
              className={`px-2.5 py-1 text-xs rounded-lg font-medium transition-colors ${
                capacityFilter === 'Overloaded'
                  ? 'bg-rose-600 text-white shadow-xs'
                  : 'bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-100'
              }`}
            >
              🔴 Overloaded ({capacitySummary?.overloaded_count || 0})
            </button>
          </div>
        </div>

        {/* Heatmap Visual Strip */}
        {capacityData.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5 pt-1">
            {capacityData.slice(0, 6).map((c) => {
              const isOverloaded = c.capacity_status === 'Overloaded';
              const isHeavy = c.capacity_status === 'Heavy';

              return (
                <div
                  key={c.id}
                  className={`rounded-xl p-2.5 border text-xs flex flex-col justify-between ${
                    isOverloaded
                      ? 'bg-rose-50/80 border-rose-200'
                      : isHeavy
                      ? 'bg-amber-50/80 border-amber-200'
                      : 'bg-white border-slate-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-800 truncate max-w-[80px]" title={c.name}>
                      {c.name.split(' ')[0]}
                    </span>
                    <span
                      className={`text-[9px] font-bold px-1 rounded ${
                        isOverloaded
                          ? 'bg-rose-200 text-rose-800'
                          : isHeavy
                          ? 'bg-amber-200 text-amber-800'
                          : 'bg-emerald-100 text-emerald-800'
                      }`}
                    >
                      {c.capacity_status}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200/80 rounded-full h-1.5 my-1.5 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        isOverloaded ? 'bg-rose-500' : isHeavy ? 'bg-amber-500' : 'bg-emerald-500'
                      }`}
                      style={{ width: `${Math.min(100, (c.weekly_hours / 40) * 100)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-slate-500">
                    <span>{c.active_tasks} tasks</span>
                    <span>{c.weekly_hours}h</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-3 rounded-lg border border-slate-200">
        <div className="flex items-center space-x-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search team..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 h-9 text-xs"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-xs text-slate-700"
          >
            <option value="">All Roles</option>
            <option value="owner">Owner</option>
            <option value="manager">Manager</option>
            <option value="developer">Developer</option>
            <option value="team_member">Team Member</option>
            <option value="viewer">Viewer</option>
          </select>
        </div>
        <div className="flex items-center space-x-4 text-xs text-slate-500">
          <span className="font-medium text-slate-400">Sort by:</span>
          <SortButton label="Name" field="name" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          <SortButton label="Role" field="role" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          <SortButton label="Projects" field="projects_count" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          <SortButton label="Tasks" field="active_tasks_count" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          <SortButton label="Last Active" field="last_login_at" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
        </div>
      </div>

      {/* Team Cards Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : paginatedMembers.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-slate-500">
            No team members found matching your search.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedMembers.map((m) => {
              const isDeactivated = m.is_active === false;
              const cap = capacityData.find((c) => c.id === m.id || c.email === m.email);
              const isOverloaded = cap?.capacity_status === 'Overloaded';
              const isHeavy = cap?.capacity_status === 'Heavy';
              const weeklyHours = cap ? cap.weekly_hours : 0;
              const pct = Math.min(100, Math.round((weeklyHours / 40) * 100));

              return (
                <Card
                  key={m.id}
                  onClick={() => handleOpenDetail(m)}
                  className={`cursor-pointer transition-all hover:border-brand-teal/60 hover:shadow-sm ${
                    isDeactivated ? 'opacity-60 bg-slate-50' : 'bg-white'
                  }`}
                >
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center font-bold text-sm text-brand-navy">
                          {m.name.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-semibold text-sm text-brand-navy flex items-center space-x-1.5">
                            <span>{m.name}</span>
                            {isDeactivated && (
                              <span className="text-[10px] text-red-600 bg-red-50 border border-red-200 px-1 rounded font-normal">
                                Deactivated
                              </span>
                            )}
                          </h3>
                          <p className="text-xs text-slate-500">{m.email}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 pt-2 border-t border-slate-100">
                      <Badge variant="secondary" className="capitalize text-[10px]">
                        {m.role.replace('_', ' ')}
                      </Badge>
                      <span className="text-xs text-slate-400">•</span>
                      <span className="text-xs text-slate-500">{m.timezone || 'Asia/Karachi'}</span>
                    </div>

                    <div className="flex items-center space-x-1.5 pt-1 text-xs">
                      <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      <span className={m.last_login_at ? 'text-slate-600 font-medium' : 'text-slate-400 italic'}>
                        {formatRelativeLastLogin(m.last_login_at)}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 text-xs">
                      <div>
                        <span className="text-slate-400">Projects:</span>
                        <span className="font-bold text-slate-700 ml-1">{m.projects_count || 0}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Active Tasks:</span>
                        <span className="font-bold text-slate-700 ml-1">{m.active_tasks_count || 0}</span>
                      </div>
                    </div>

                    {cap && (
                      <div className="pt-2 border-t border-slate-100 space-y-1.5 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-400 font-medium">Workload:</span>
                          <span
                            className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                              isOverloaded
                                ? 'bg-rose-100 text-rose-700 border border-rose-200'
                                : isHeavy
                                ? 'bg-amber-100 text-amber-700 border border-amber-200'
                                : 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                            }`}
                          >
                            {cap.capacity_status} ({cap.weekly_hours}h / 40h)
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              isOverloaded ? 'bg-rose-500' : isHeavy ? 'bg-amber-500' : 'bg-emerald-500'
                            }`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <Pagination
            totalItems={sortedMembers.length}
            currentPage={validCurrentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Member Detail Drawer */}
      {selectedMember && (
        <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col border-l border-slate-200 animate-in slide-in-from-right duration-200">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50/50">
            <div className="flex items-center space-x-2">
              <span className="font-bold text-sm text-brand-navy">Member Profile</span>
              <Badge variant="secondary" className="capitalize text-[10px]">
                {selectedMember.role.replace('_', ' ')}
              </Badge>
              {selectedMember.is_active === false && (
                <Badge variant="destructive" className="text-[10px]">
                  Deactivated
                </Badge>
              )}
            </div>
            <button onClick={handleCloseDetail} className="text-slate-400 hover:text-slate-700 p-1 rounded-md">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Drawer Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {actionError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-start space-x-2">
                <AlertTriangle className="h-4 w-4 shrink-0 text-red-600 mt-0.5" />
                <span>{actionError}</span>
              </div>
            )}

            <div className="flex items-center space-x-4 pb-4 border-b border-slate-100">
              <div className="h-16 w-16 rounded-full bg-brand-navy text-white flex items-center justify-center font-bold text-xl">
                {selectedMember.name.substring(0, 2).toUpperCase()}
              </div>
              <div>
                <h2 className="text-lg font-bold text-brand-navy">{selectedMember.name}</h2>
                <p className="text-xs text-slate-500">{selectedMember.email}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Timezone: {selectedMember.timezone || 'Asia/Karachi'}</p>
              </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <span className="text-slate-400 block">Assigned Projects</span>
                <span className="text-lg font-bold text-slate-800">{selectedMember.projects_count || 0}</span>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <span className="text-slate-400 block">Active Tasks</span>
                <span className="text-lg font-bold text-slate-800">{selectedMember.active_tasks_count || 0}</span>
              </div>
            </div>

            {/* Login & Device Activity */}
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-2 text-xs">
              <h4 className="font-bold uppercase tracking-wider text-slate-700 flex items-center space-x-1.5 text-[11px]">
                <Clock className="h-3.5 w-3.5 text-brand-teal" />
                <span>Last Login & Device Activity</span>
              </h4>
              <div className="grid grid-cols-1 gap-2 pt-1">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Last Active:</span>
                  <span className="font-semibold text-slate-800">
                    {formatRelativeLastLogin(selectedMember.last_login_at)}
                  </span>
                </div>
                {selectedMember.last_login_at && !isNaN(new Date(selectedMember.last_login_at).getTime()) && (
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-slate-500">Timestamp:</span>
                    <span className="font-mono text-slate-700">
                      {new Date(selectedMember.last_login_at).toLocaleString('en-US', {
                        timeZone: 'Asia/Karachi',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}{' '}
                      PKT
                    </span>
                  </div>
                )}
                {selectedMember.last_login_ip && (
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-slate-500">Last IP Address:</span>
                    <span className="font-mono text-slate-700 bg-white px-1.5 py-0.5 rounded border border-slate-200">
                      {selectedMember.last_login_ip}
                    </span>
                  </div>
                )}
                {selectedMember.last_login_user_agent && (
                  <div className="pt-1 text-[11px] text-slate-500">
                    <span className="font-medium text-slate-700 block mb-0.5">Device & User Agent:</span>
                    <span className="font-mono text-[10px] text-slate-600 block bg-white p-1.5 rounded border border-slate-200 break-all leading-normal">
                      {selectedMember.last_login_user_agent}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Owner Role Management */}
            {isOwner && (
              <div className="space-y-4 pt-4 border-t border-slate-100">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center space-x-1.5">
                  <Shield className="h-3.5 w-3.5 text-brand-teal" />
                  <span>Role Management (Owner Only)</span>
                </h4>
                <div className="flex items-center space-x-2">
                  <select
                    value={drawerRole}
                    onChange={(e) => setDrawerRole(e.target.value)}
                    className="flex-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs text-slate-800"
                  >
                    <option value="owner">Owner</option>
                    <option value="manager">Manager</option>
                    <option value="developer">Developer</option>
                    <option value="team_member">Team Member</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <Button
                    size="sm"
                    onClick={handleUpdateRole}
                    disabled={drawerRole === selectedMember.role}
                    className="h-9 text-xs bg-brand-teal text-white hover:bg-brand-teal-action"
                  >
                    Update Role
                  </Button>
                </div>
              </div>
            )}

            {/* Member Lifecycle Management */}
            {isOwner && (
              <div className="space-y-3 pt-4 border-t border-slate-100">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">Account Access</h4>
                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleToggleDeactivate}
                    className={`text-xs justify-start ${
                      selectedMember.is_active === false
                        ? 'text-emerald-700 border-emerald-200 hover:bg-emerald-50'
                        : 'text-amber-700 border-amber-200 hover:bg-amber-50'
                    }`}
                  >
                    {selectedMember.is_active === false ? (
                      <>
                        <UserCheck className="h-4 w-4 mr-2" />
                        <span>Reactivate Member</span>
                      </>
                    ) : (
                      <>
                        <UserX className="h-4 w-4 mr-2" />
                        <span>Deactivate Member</span>
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRemoveMember}
                    className="text-xs justify-start text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    <span>Remove Member From Workspace</span>
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400">
                  Note: The last active owner cannot be demoted, deactivated, or removed.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setShowInviteModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50/75">
              <div className="flex items-center space-x-2">
                <UserPlus className="h-5 w-5 text-brand-teal" />
                <h2 className="font-bold text-base text-brand-navy">Invite Team Member</h2>
              </div>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-md"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleInviteSubmit} className="p-6 space-y-4">
              {inviteError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-start space-x-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-red-600 mt-0.5" />
                  <span>{inviteError}</span>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Full Name *</label>
                <Input
                  required
                  placeholder="e.g. Sarah Jenkins"
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  className="h-9 text-xs"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Email Address *</label>
                <Input
                  type="email"
                  required
                  placeholder="sarah@agency.com"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="h-9 text-xs"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Role *</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-xs text-slate-800 font-medium"
                >
                  <option value="manager">Manager (Workload & Team Oversight)</option>
                  <option value="developer">Developer (Task & Code Delivery)</option>
                  <option value="team_member">Team Member (General Delivery)</option>
                  <option value="viewer">Viewer (Read-Only Transparency)</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-700">Temporary Password *</label>
                  <button
                    type="button"
                    onClick={handleRegeneratePassword}
                    className="text-[11px] text-brand-teal hover:underline flex items-center space-x-1"
                  >
                    <RefreshCw className="h-3 w-3" />
                    <span>Regenerate</span>
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  <Input
                    readOnly
                    value={inviteForm.password}
                    className="h-9 text-xs font-mono bg-slate-50 flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCopyPassword}
                    className="h-9 px-3 text-xs shrink-0 flex items-center space-x-1"
                    title="Copy Password"
                  >
                    {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                    <span>{copied ? 'Copied' : 'Copy'}</span>
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400">
                  Share this temporary password with the member so they can log in.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-3 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowInviteModal(false)}
                  className="text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={inviteSubmitting}
                  className="text-xs bg-brand-teal text-white hover:bg-brand-teal-action"
                >
                  {inviteSubmitting ? 'Sending...' : 'Send Invitation'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Shared Delete Confirmation Dialog */}
      <DeleteConfirmModal
        isOpen={!!deleteMemberTarget}
        itemType="Team Member"
        itemName={deleteMemberTarget?.name}
        confirmLabel="Remove Member"
        onClose={() => setDeleteMemberTarget(null)}
        onConfirm={confirmRemoveMember}
      />
    </div>
  );
}
